import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const TemplateComparison = ({ projectId, documents, onMerged }) => {
  const templates = useMemo(
    () => (documents || []).filter((doc) => doc.document_type === 'template'),
    [documents]
  );
  const practiceA = useMemo(
    () => (documents || []).find((doc) => doc.document_type === 'practice_a'),
    [documents]
  );
  const practiceB = useMemo(
    () => (documents || []).find((doc) => doc.document_type === 'practice_b'),
    [documents]
  );

  const [featuresByDoc, setFeaturesByDoc] = useState({});
  const [loading, setLoading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [selected, setSelected] = useState({});

  const loadFeatures = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/projects/${projectId}/template-features`);
      setFeaturesByDoc(data || {});
    } catch {
      setFeaturesByDoc({});
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const handleAnalyse = async () => {
    setAnalysing(true);
    try {
      await api.post(`/api/projects/${projectId}/analyse-templates`);
      toast.success('Template analysis complete');
      await loadFeatures();
    } catch {
      toast.error('Template analysis failed');
    } finally {
      setAnalysing(false);
    }
  };

  const handleCherryPickMerge = async () => {
    const selectedFeatures = Object.keys(selected)
      .filter((key) => selected[key])
      .map((key) => {
        const [documentId, featureKey] = key.split('::');
        return { document_id: Number(documentId), feature_key: featureKey };
      });

    if (selectedFeatures.length === 0) {
      toast.error('Select at least one feature to merge');
      return;
    }

    setMerging(true);
    try {
      const { data } = await api.post(`/api/projects/${projectId}/cherry-pick-merge`, {
        selected_features: selectedFeatures,
        practice_a_doc_id: practiceA?.id || null,
        practice_b_doc_id: practiceB?.id || null,
      });
      toast.success('Cherry-pick merge complete');
      onMerged?.(data);
    } catch {
      toast.error('Cherry-pick merge failed');
    } finally {
      setMerging(false);
    }
  };

  if (templates.length < 2) {
    return null;
  }

  return (
    <section className="panel panel--spaced template-comparison">
      <div className="template-comparison__header">
        <h3>Template Comparison</h3>
        <button className="btn" onClick={handleAnalyse} disabled={analysing}>
          {analysing ? 'Analysing...' : 'Analyse Templates'}
        </button>
      </div>

      {loading && <p className="muted">Loading template features...</p>}

      <div className="template-columns">
        {templates.map((template) => {
          const features = featuresByDoc[String(template.id)] || [];
          return (
            <article className="template-column" key={template.id}>
              <h4>{template.filename || `Template ${template.id}`}</h4>
              {features.length === 0 && <p className="muted">No extracted features yet.</p>}
              {features.map((feature) => {
                const key = `${template.id}::${feature.feature_key}`;
                return (
                  <label key={feature.id || key} className="feature-card">
                    <input
                      type="checkbox"
                      checked={!!selected[key]}
                      onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <span className="feature-card__title">{feature.feature_title}</span>
                    <span className="feature-card__summary">{feature.feature_summary}</span>
                  </label>
                );
              })}
            </article>
          );
        })}
      </div>

      <button className="btn btn--primary" onClick={handleCherryPickMerge} disabled={merging}>
        {merging ? 'Generating...' : 'Generate Policy from Selected Features'}
      </button>
    </section>
  );
};

export default TemplateComparison;
